import React from 'react';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router';
import DecisionTreeExpandedContextProvider from '../DecisionTreeExpandedContext/DecisionTreeExpandedContextProvider';
import DecisionTreeInfoContextProvider from '../DecisionTreeInfoContext/DecisionTreeInfoContextProvider';
import { DecisionTreePreviewContextProvider } from '../DecisionTreePreviewContext';
import DecisionTreeResultContextProvider from '../DecisionTreeResultContext/DecisionTreeResultContextProvider';
import DecisionTreeRunContextProvider from '../DecisionTreeRunContext/DecisionTreeRunContextProvider';
import DecisionTreeSequenceTransitionContextProvider from '../DecisionTreeSequenceTransitionContext/DecisionTreeSequenceTransitionContextProvider';
import { DecisionTreeContext } from './useDecisionTreeContext';

export default function DecisionTreeContextProvider({ children }: { children: React.ReactNode }) {
  const [cookies, setCookies, removeCookies] = useCookies(['decisionTreeMountPreference', 'show-decision-tree']);

  const location = useLocation();
  const previousLocation = React.useRef(location.pathname);

  const isMounted = cookies['show-decision-tree'] as 'main' | 'split' | null | undefined;

  const setIsMounted = React.useCallback((value: 'main' | 'split' | null | undefined) => {
    if (value) { setCookies('show-decision-tree', value); } else { removeCookies('show-decision-tree'); }
  }, [setCookies, removeCookies]);

  React.useEffect(() => {
    if (isMounted && cookies.decisionTreeMountPreference) {
      setIsMounted(cookies.decisionTreeMountPreference);
    }
  }, [isMounted, cookies.decisionTreeMountPreference]);

  const parsedParam: 'main' | 'split' | null = React.useMemo(() => {
    if (isMounted === 'main') return 'main';
    if (isMounted === 'split') return 'split';
    return null;
  }, [isMounted]);

  const toggleMounted = React.useCallback(() => {
    if (parsedParam) {
      setIsMounted(undefined);
    } else {
      setIsMounted(cookies.decisionTreeMountPreference || 'split');
    }
  }, [parsedParam, cookies.decisionTreeMountPreference]);

  const setPreference = React.useCallback((newValue: 'main' | 'split') => {
    setCookies('decisionTreeMountPreference', newValue);
    setIsMounted(newValue);
  }, [setCookies, setIsMounted]);

  React.useEffect(() => {
    if (location.pathname !== previousLocation.current) {
      if (parsedParam === 'main') {
        setIsMounted(null);
      }
    }
    previousLocation.current = location.pathname;
  }, [parsedParam, location.pathname, setIsMounted]);

  const value = React.useMemo(() => ({
    isMounted: parsedParam,
    toggleMounted,
    setPreference,
  }), [parsedParam, cookies.decisionTreeMountPreference]);

  return (
    <DecisionTreeContext.Provider value={value}>
      <DecisionTreeRunContextProvider>
        <DecisionTreePreviewContextProvider>
          <DecisionTreeInfoContextProvider>
            <DecisionTreeExpandedContextProvider>
              <DecisionTreeResultContextProvider>
                <DecisionTreeSequenceTransitionContextProvider>
                  {children}
                </DecisionTreeSequenceTransitionContextProvider>
              </DecisionTreeResultContextProvider>
            </DecisionTreeExpandedContextProvider>
          </DecisionTreeInfoContextProvider>
        </DecisionTreePreviewContextProvider>
      </DecisionTreeRunContextProvider>
    </DecisionTreeContext.Provider>
  );
}
